import React, {
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
} from "react";

import { faLink } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Typography from "@material-ui/core/Typography";
import { Formik, FormikTouched, useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import isEqual from "lodash/isEqual";

import { ConfirmDialog } from "components/ConfirmDialog";
import { AdminLink } from "components/AdminLink/AdminLink";
import AuditLogLink from "components/AuditLog/AuditLogLink";
import { MultiButton } from "components/Button";
import { DeploymentBusinessForm } from "components/Business/BusinessForm/DeploymentBusinessForm";
import { TemporaryBusinessesDialog } from "components/Business/TemporaryBusinesses";
import { Column } from "components/Layout";
import { AddButton as SyncButton } from "components/Button/CollapseInlineButton";
import { Button, SecondaryButton } from "components/Form";
import { mapIncomingTerms } from "components/Form/FormikControls/TradingTerms/utils";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";

import { AuditLogTypes } from "constants/auditLog";
import { CalculationBasis } from "constants/interest";
import { ApiModel } from "constants/loading";
import { DeploymentPermission } from "constants/permissions";
import { Settings } from "constants/settings";

import { withRegistration } from "lib/businessRegistrations";
import { getBusinessSubType } from "lib/businesses";
import {
  mapBusinessValuesToInsuranceType,
  mapDefaultDeploymentTransitInsuranceToBusinessValues,
} from "lib/insuranceRates";
import {
  getSelectSyncDataHashRoute,
  openEditBusinessModal,
} from "lib/navigation";

import {
  getActiveLivestockAgentDeployment,
  getActiveSaleyardAdminSaleyard,
  getBusinessRelationsByBusinessId,
  getBusinessTradingTermsById,
  getContextByModalType,
  getDefaultBillingSettings,
  getDefaultBuyerSplitByBusinessId,
  getDefaultVendorSplitByBusinessId,
  getInterestSettingsByBusinessId,
  getInterestSettingsByDeploymentId,
  getIsLivestockAgent,
  getIsSaleyardAdmin,
  getPropertyEnrichedBusinessByBusinessId,
  getVendorCommissionBandsByBusinessId,
  selectRoleDeployments,
} from "selectors";
import { getBusinessRegistrationById } from "selectors/businessRegistrations";

import {
  useHasDefaultBuyerSplitPermission,
  useHasDefaultVendorSplitPermission,
  useHasDeploymentPermission,
  useSomeHasPermission,
} from "hooks/useHasPermission";
import { useBoolean } from "hooks/useBoolean";

import noop from "lodash/noop";

import {
  computeInitialValues,
  ModalContextWithSnapshot,
} from "sagas/modals/util";
import { ModalTypes } from "constants/navigation";
import { isEmpty } from "lodash";
import { submitBusinessForm } from "actions/modals/business";

import { getBusinessValidationSchema } from "./validation";
import { BusinessFormValues, DefaultSplit } from "./types";
import { mergeUntouched } from "./utils";

interface BusinessFormProps {
  closeSelf: () => void;
  business: Business | null;
  section?: string;
  handleSubmit?: (
    values: BusinessFormValues,
    submittedTouched: FormikTouched<BusinessFormValues>,
    closeAfterSave?: boolean,
  ) => void;
  defaultAddressSearch?: string;
  baselineValues?: Partial<BusinessFormValues>;
  // Note this gets propdrilled with a {...props}
  openSideLoadedBusinessModal?: boolean;
  sideLoadedBusinessId?: string;
  onSideLoadedSuggestionsCancel?: () => void;
  activateTemporary?: boolean;
}

interface EditBusinessProps {
  onClose: () => void;
}

const blankDefaults: Partial<BusinessFormValues> = {
  address: {},
  bankingDetails: null,
  businessUsers: [],
  buyerWays: [],
  emailRecipients: [],
  generateBpayCRN: true,
  isActive: true,
  isBuyer: false,
  isTopBuyerCattle: false,
  isTopBuyerSheep: false,
  isTransporter: false,
  isVendor: false,
  properties: [],
  relationships: [],
  shortCodeSaleyard: "",
  shortCode: "",
  shortCodeAuctionsPlus: "",
  vendorCommissionBands: [],
  defaultBuyerSplit: undefined,
  defaultVendorSplit: undefined,
  interestSettings: [],
  labelIds: [],
};

const useBuildInitialValues = (
  /**
   * Returns both the effective initialValues (including restored from modal context snapshot)
   * and a "baseline" version with the snapshot stripped, used to detect
   * whether a restored form differs from the original business state.
   */
  context: Partial<ModalContext>,
  hasVendorCommissionsFeature: boolean,
  hasDefaultVendorSplitsFeature: boolean,
  hasDefaultBuyerSplitsFeature: boolean,
  hasRelationshipsFeature: boolean,
  business?: Business,
) => {
  const businessId: string = business?.id || "";

  const vendorCommissionBandsRaw: VendorCommissionBand[] = useSelector(
    getVendorCommissionBandsByBusinessId(businessId),
  );

  const defaultVendorSplit: DefaultSplit = useSelector(
    getDefaultVendorSplitByBusinessId(businessId),
  );

  const defaultBuyerSplit: DefaultSplit = useSelector(
    getDefaultBuyerSplitByBusinessId(businessId),
  );

  const interestSettingsRaw: InterestSettings[] = useSelector(
    getInterestSettingsByBusinessId(businessId),
  );
  const deploymentId = useSelector(getActiveLivestockAgentDeployment)?.id;
  const deploymentInterestSettings: InterestSettings[] | undefined =
    useSelector(getInterestSettingsByDeploymentId(deploymentId));
  // A new override starts on the agency's basis, as the backend does when none
  // is sent.
  const defaultCalculationBasis =
    deploymentInterestSettings?.[0]?.calculationBasis ??
    CalculationBasis.PER_DOCUMENT;

  const relationshipsRaw: BusinessRelation[] = useSelector(
    getBusinessRelationsByBusinessId(businessId),
  );

  const tradingTermsLookup = useSelector(getBusinessTradingTermsById);

  const billingSettings = useSelector(getDefaultBillingSettings);

  const hasInterestFeature = useHasDeploymentPermission(
    DeploymentPermission.featureInterest,
  );

  const defaultInterestId = React.useMemo(() => uuidv4(), []);

  // TODO: to be migration into a helper function passed into computeInitialValues
  const applyDerivedValues = useCallback(
    (target: Partial<BusinessFormValues>) => {
      // Vendor commission bands
      if (hasVendorCommissionsFeature) {
        target.vendorCommissionBands = vendorCommissionBandsRaw;
      }

      // Default splits
      if (hasDefaultVendorSplitsFeature) {
        target.defaultVendorSplit = defaultVendorSplit || undefined;
      }
      if (hasDefaultBuyerSplitsFeature) {
        target.defaultBuyerSplit = defaultBuyerSplit || undefined;
      }

      // Interest
      if (hasInterestFeature) {
        const interestSettings = interestSettingsRaw ?? [];

        if (interestSettings.length === 0) {
          target.interestSettings = [
            {
              debitInterestRate: 0,
              creditInterestRate: 0,
              daysOverdueThreshold: 0,
              calculationBasis: defaultCalculationBasis,
              id: defaultInterestId,
            },
          ];
        } else {
          target.interestSettings = interestSettings;
        }
      }

      // Relationships
      if (hasRelationshipsFeature) {
        target.relationships = relationshipsRaw ?? [];
      } else {
        // As the API may still be backward compatibily sending the relationships down in the
        // business payload, and we don't want to send it back unless we're certain - if I don't
        // have the permission make sure I don't throw it back.
        target.relationships = undefined;
      }

      // Trading terms
      const tradingTerms = (tradingTermsLookup?.[business?.id] ?? []).map(
        mapIncomingTerms,
      );

      target.tradingTerms = tradingTerms;

      // Billing / transit insurance mapping
      const transitInsuranceDefault =
        billingSettings?.transitInsuranceApplicationDefault;

      if (business) {
        // Editing: the business's own values decide what is shown. The Billing
        // Settings default is a fallback only, so an agency or saleyard without
        // one still sees what the business has stored.
        target.transitInsuranceApplicationDefault =
          mapBusinessValuesToInsuranceType(target) || transitInsuranceDefault;
      } else if (billingSettings) {
        // Creating: seed from the Billing Settings default.
        target.transitInsuranceApplicationDefault = transitInsuranceDefault;

        Object.assign(
          target,
          mapDefaultDeploymentTransitInsuranceToBusinessValues(
            transitInsuranceDefault,
          ),
        );
      }
    },
    [
      billingSettings,
      business,
      defaultBuyerSplit,
      defaultCalculationBasis,
      defaultVendorSplit,
      hasDefaultBuyerSplitsFeature,
      hasDefaultVendorSplitsFeature,
      hasInterestFeature,
      hasRelationshipsFeature,
      hasVendorCommissionsFeature,
      interestSettingsRaw,
      relationshipsRaw,
      tradingTermsLookup,
      vendorCommissionBandsRaw,
      defaultInterestId,
    ],
  );

  const initialValues = React.useMemo(() => {
    const initial = computeInitialValues(blankDefaults, context, business);

    applyDerivedValues(initial);

    return initial;
  }, [context, business, applyDerivedValues]);

  return initialValues;
};

function ReinitializeUntouched({
  baselineValues,
}: {
  baselineValues: Partial<BusinessFormValues>;
}) {
  const formik = useFormikContext<Partial<BusinessFormValues>>();
  const {
    values,
    touched,
    setValues,
    resetForm,
    initialValues: formikInitialValues,
  } = formik;
  const previousInitialValues = useRef(formikInitialValues);

  // Update baseline when form is reset (e.g. after a save)
  useEffect(() => {
    previousInitialValues.current = formikInitialValues;
  }, [formikInitialValues]);

  useEffect(() => {
    if (isEqual(previousInitialValues.current, baselineValues)) return;
    const nextValues = mergeUntouched(
      baselineValues,
      values,
      touched,
      previousInitialValues.current,
    );

    // Sync initialValues to the new store state, then restore user edits to values.
    // resetForm sets Formik's internal initialValues to its first argument's values.
    resetForm({
      values: baselineValues,
      touched,
    });
    // resetForm also overwrites current values with baselineValues as a side effect,
    // so setValues re-applies the untouched-merge on top of the new baseline.
    setValues(nextValues, false);

    previousInitialValues.current = baselineValues;
  }, [baselineValues, values, touched, setValues, resetForm]);
  return null;
}

const Form = (props: BusinessFormProps): React.JSX.Element => {
  const {
    closeSelf,
    business,
    defaultAddressSearch,
    handleSubmit,
    section,
    baselineValues,
    onSideLoadedSuggestionsCancel,
    activateTemporary,
  } = props;

  const [isConfirmCloseOpen, openConfirmClose, closeConfirmClose] =
    useBoolean(false);

  const formikProps = useFormikContext<BusinessFormValues>();
  const { dirty, isValid, resetForm, values, touched } = formikProps;

  const hasSyncAllPermission = useSomeHasPermission(
    selectRoleDeployments,
    DeploymentPermission.featureSyncAllAlternativeBusinessFields,
  );

  // Activating from side-loaded data uses the full editor, and saving makes
  // the business permanent.
  const isActivating =
    Boolean(business?.isTemporary) && Boolean(activateTemporary);

  const [isSubFormActive, setIsSubFormActive] = React.useState<boolean>(false);

  const doHandleClose = (): void => {
    if (dirty) {
      openConfirmClose();
    } else {
      closeSelf();
    }
  };

  const handleSideLoadedSuggestionsCancel =
    onSideLoadedSuggestionsCancel &&
    ((): void => {
      if (dirty) {
        openConfirmClose();
      } else {
        onSideLoadedSuggestionsCancel();
      }
    });

  const submitDisabled =
    !((dirty || isActivating) && isValid) || isSubFormActive;

  const titleActionString: string = business
    ? `Edit ${getBusinessSubType(business)} "${business.name}"`
    : "Create Business";
  const submitTitle = "Submit";

  const isLivestockAgent = useSelector(getIsLivestockAgent);
  const isSaleyardAdmin = useSelector(getIsSaleyardAdmin);

  const parentObjectSelector = isLivestockAgent
    ? getActiveLivestockAgentDeployment
    : getActiveSaleyardAdminSaleyard;
  const parentObject = useSelector(parentObjectSelector) || {};

  const parentObjectId = parentObject.id;

  const auditLogLinkProps = useMemo(() => {
    if (isLivestockAgent) {
      return {
        auditLogType: AuditLogTypes.DEPLOYMENT_BUSINESS,
        queryParams: {
          deploymentId: parentObjectId,
        },
      };
    }
    if (isSaleyardAdmin) {
      return {
        auditLogType: AuditLogTypes.SALEYARD_BUSINESS,
        queryParams: {
          saleyardId: parentObjectId,
        },
      };
    }
    return {
      auditLogType: undefined,
    };
  }, [isLivestockAgent, isSaleyardAdmin, parentObjectId]);

  // Only touched keys are patched, so activation marks its fields touched.
  const getSubmission = (): [
    BusinessFormValues,
    FormikTouched<BusinessFormValues>,
  ] =>
    isActivating
      ? [
          { ...values, isTemporary: false, isActive: true },
          { ...touched, isTemporary: true, isActive: true },
        ]
      : [values, touched];

  const saveAndClose = () => {
    if (!submitDisabled) {
      handleSubmit(...getSubmission());
    }
  };

  const saveAndKeepOpen = () => {
    if (!submitDisabled) {
      handleSubmit(...getSubmission(), false);
      resetForm({
        values,
        touched: {},
      });
    }
  };

  return (
    <>
      <ZoomyDialog open onClose={doHandleClose} maxWidth="xl" fullWidth>
        <WaitForSync
          requiredData={[
            ApiModel.BUSINESSES,
            ApiModel.AGENCIES,
            ApiModel.TRADING_TERMS,
            ApiModel.VENDOR_COMMISSION_BANDS,
          ]}
        >
          <ReinitializeUntouched baselineValues={baselineValues} />
          <DialogTitle onClose={doHandleClose}>
            {business && (
              <AuditLogLink
                dataId={business.id}
                returnTo={window.location.hash}
                {...auditLogLinkProps}
              />
            )}
            &nbsp;
            {titleActionString}{" "}
            {hasSyncAllPermission && business && (
              <SyncButton
                to={`#${getSelectSyncDataHashRoute(
                  business.id,
                  window.location.hash,
                )}`}
                data-tour=""
              >
                <FontAwesomeIcon icon={faLink} /> Sync All
              </SyncButton>
            )}
            <AdminLink model="business" cbid={business?.id} />
          </DialogTitle>
          <DialogContent dividers form="true">
            {isActivating && (
              <Column padding={2} marginVertical={1} background="gray95">
                <Typography>
                  Choose any legacy data to add, then save to activate this
                  temporary business as a new, permanent business.
                </Typography>
              </Column>
            )}
            <DeploymentBusinessForm
              {...props}
              onSideLoadedSuggestionsCancel={handleSideLoadedSuggestionsCancel}
              setIsSubFormActive={setIsSubFormActive}
              defaultAddressSearch={defaultAddressSearch}
              section={section}
            />
          </DialogContent>
          <DialogActions>
            <SecondaryButton onClick={closeSelf}>Cancel</SecondaryButton>
            {business ? (
              <MultiButton
                buttons={[
                  {
                    title: "Save",
                    dataTour: "save",
                    isDisabled: submitDisabled,
                    onClick: saveAndKeepOpen,
                  },
                  {
                    title: "Save & Close",
                    dataTour: "submit",
                    default: true,
                    isDisabled: submitDisabled,
                    onClick: saveAndClose,
                  },
                ]}
                flex
                buttonInline={false}
                stickySetting={Settings.businessFormSaveAction}
              />
            ) : (
              <Button
                data-tour="submit"
                onClick={saveAndClose}
                disabled={submitDisabled}
              >
                {submitTitle}
              </Button>
            )}
          </DialogActions>
        </WaitForSync>
      </ZoomyDialog>
      <ConfirmDialog
        title="Are you sure you want to exit?"
        message="You will lose your unsaved changes."
        isOpen={isConfirmCloseOpen}
        onCancel={closeConfirmClose}
        onDelete={closeSelf}
        buttonMessage="Confirm"
        cancelMessage="Back"
      />
    </>
  );
};

type ModalContext = ModalContextWithSnapshot<BusinessFormValues> & {
  businessId: string;
  section: string;
  defaultAddressSearch: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Formik snapshot from saga
  formikSnapshot: any;
  openSideLoadedBusinessModal?: boolean;
  sideLoadedBusinessId?: string;
  // Set when suggestions are the reason the modal was opened, so backing out
  // of them backs out of the modal too.
  closeOnSideLoadedSuggestionsCancel?: boolean;
  activateTemporary?: boolean;
};

const EditBusiness = ({ onClose }: EditBusinessProps): React.JSX.Element => {
  const context: ModalContext =
    useSelector(getContextByModalType(ModalTypes.EditBusiness)) || {};

  const {
    businessId,
    section,
    defaultAddressSearch,
    openSideLoadedBusinessModal,
    sideLoadedBusinessId,
    closeOnSideLoadedSuggestionsCancel,
    activateTemporary,
  } = context;

  const dispatch = useDispatch();
  // Once resolved into an active Business, it's edited here.
  const [resolvedBusinessId, setResolvedBusinessId] = useState<string>();

  const editingBusiness = useSelector(
    getPropertyEnrichedBusinessByBusinessId(businessId),
  );
  const registration = useSelector(
    getBusinessRegistrationById(editingBusiness?.registrationId),
  );
  // The form edits the ABN/ACN and shows the ABR details, all of which live on the
  // registration - a re-check updates the form through this.
  const business = useMemo(
    () =>
      isEmpty(editingBusiness)
        ? undefined
        : withRegistration(
            editingBusiness,
            registration ? { [registration.id]: registration } : {},
          ),
    [editingBusiness, registration],
  );

  const handleSubmit = useCallback(
    (
      submittedValues: Partial<BusinessFormValues>,
      touched: FormikTouched<BusinessFormValues>,
      closeAfterSave = true,
    ) => {
      dispatch(submitBusinessForm(businessId, submittedValues, touched));
      if (closeAfterSave) {
        onClose();
      }
    },
    [dispatch, businessId, onClose],
  );

  const hasVendorCommissionsFeature = useHasDeploymentPermission(
    DeploymentPermission.featureVendorCommissions,
  );

  const hasDefaultBuyerSplitsFeature = useHasDefaultBuyerSplitPermission();
  const hasDefaultVendorSplitsFeature = useHasDefaultVendorSplitPermission();
  const hasRelationshipsFeature = useHasDeploymentPermission(
    DeploymentPermission.featureBusinessRelations,
  );

  // Memoised contexts to prevent formik enableReinitialization from wiping
  // user edits when switching sections
  const initialValuesContext = React.useMemo(
    () => ({
      values: context.values,
      formikSnapshot: context.formikSnapshot,
      businessId: context.businessId,
    }),
    [context.values, context.formikSnapshot, context.businessId],
  );

  const { formikSnapshot, businessId: contextBusinessId } = context;

  const baselineValues = useBuildInitialValues(
    { businessId: contextBusinessId },
    hasVendorCommissionsFeature,
    hasDefaultVendorSplitsFeature,
    hasDefaultBuyerSplitsFeature,
    hasRelationshipsFeature,
    business,
  );

  const initialValues = useBuildInitialValues(
    initialValuesContext,
    hasVendorCommissionsFeature,
    hasDefaultVendorSplitsFeature,
    hasDefaultBuyerSplitsFeature,
    hasRelationshipsFeature,
    business,
  );
  // Temporary Businesses are resolved, not edited - unless activating from
  // legacy data. Decided once per Business and mode, so the form doesn't
  // flash up as the dialog closes after resolving.
  const isTemporaryRef = useRef<{
    businessId: string;
    activateTemporary: boolean;
    isTemporary: boolean;
  }>();
  if (
    business &&
    (isTemporaryRef.current?.businessId !== businessId ||
      isTemporaryRef.current.activateTemporary !== Boolean(activateTemporary))
  ) {
    isTemporaryRef.current = {
      businessId,
      activateTemporary: Boolean(activateTemporary),
      isTemporary: Boolean(business.isTemporary) && !activateTemporary,
    };
  }
  if (
    isTemporaryRef.current?.businessId === businessId &&
    isTemporaryRef.current.isTemporary &&
    resolvedBusinessId !== businessId
  ) {
    return (
      <TemporaryBusinessesDialog
        businessId={businessId}
        onClose={onClose}
        onResolved={resultingBusinessId =>
          resultingBusinessId === businessId
            ? setResolvedBusinessId(businessId)
            : openEditBusinessModal(resultingBusinessId)
        }
      />
    );
  }

  return (
    <Formik<Partial<BusinessFormValues>>
      initialValues={initialValues}
      validationSchema={getBusinessValidationSchema({
        includeVendorCommissionBands: hasVendorCommissionsFeature,
        includeDefaultBuyerSplit: hasDefaultBuyerSplitsFeature,
        includeDefaultVendorSplit: hasDefaultVendorSplitsFeature,
      })}
      initialTouched={formikSnapshot?.[context?.values?.snapshotId]?.touched}
      // No-op, but required for formik.  We handle the submission through onclick.
      onSubmit={noop}
      // enableReinitialize is intentionally omitted. useBuildInitialValues pulls from
      // live Redux selectors so any Pusher update would trigger a full form reset,
      // wiping user edits. Instead, the useEffect in Form syncs untouched fields
      // individually when the business changes in the store.
    >
      <Form
        closeSelf={onClose}
        business={business}
        section={section}
        defaultAddressSearch={defaultAddressSearch}
        handleSubmit={handleSubmit}
        openSideLoadedBusinessModal={openSideLoadedBusinessModal}
        baselineValues={baselineValues}
        sideLoadedBusinessId={sideLoadedBusinessId}
        onSideLoadedSuggestionsCancel={
          closeOnSideLoadedSuggestionsCancel ? onClose : undefined
        }
        activateTemporary={activateTemporary}
      />
    </Formik>
  );
};

export default EditBusiness;
